import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import * as PIXI from 'pixi.js';
import { BACKEND_URL } from '../config';
import { useLocalization } from '../hooks/useLocalization';

// Helper function to generate random bright colors
const getRandomBrightColor = () => {
  // Generate random RGB values that are bright (high values)
  const r = Math.floor(128 + Math.random() * 127); // 128-255
  const g = Math.floor(128 + Math.random() * 127); // 128-255
  const b = Math.floor(128 + Math.random() * 127); // 128-255
  return (r << 16) | (g << 8) | b; // Convert to hex color
};

const PixiWorldViewer = forwardRef(({ worldConfig, onClose, onRefresh, onBeforeClose }, ref) => {
  const { t, language, texts } = useLocalization();
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const animationRef = useRef(null);
  const isMountedRef = useRef(true);
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [usingBonus, setUsingBonus] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [clickingAttachment, setClickingAttachment] = useState(false);
  const [clickProgress, setClickProgress] = useState(0); // Track progress for the currently clicked attachment
  const [currentAttachmentId, setCurrentAttachmentId] = useState(null); // Track which attachment is being shown
  const [pendingScoreUpdates, setPendingScoreUpdates] = useState([]); // Track pending score updates for backend
  const [localUserScore, setLocalUserScore] = useState(0); // Local score state - will be updated when worldConfig loads
  const [persistingUpdates, setPersistingUpdates] = useState(false); // Track if updates are being persisted
  
  // Interactive zoom state
  const [currentZoomLevel, setCurrentZoomLevel] = useState(1); // Current zoom level (1x to 4x)
  const [zoomedAttachment, setZoomedAttachment] = useState(null); // Currently zoomed attachment
  const [zoomTimer, setZoomTimer] = useState(null); // Timer for auto rezoom
  const [isZooming, setIsZooming] = useState(false); // Track if zoom animation is in progress
  
  // Welcome message state
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);

  // Function to handle bonus hover effects
  const handleBonusHover = (e, isEntering) => {
    const container = e.currentTarget; // Use currentTarget to get the container, not the child element
    if (isEntering) {
      container.style.transform = 'translateY(-2px) scale(1.02)';
      container.style.boxShadow = '0 8px 25px rgba(76, 175, 80, 0.3)';
      container.style.borderColor = 'rgba(76, 175, 80, 0.6)';
      container.style.backgroundColor = 'rgba(76, 175, 80, 0.1)';
    } else {
      container.style.transform = 'translateY(0) scale(1)';
      container.style.boxShadow = 'none';
      container.style.borderColor = 'rgba(76, 175, 80, 0.3)';
      container.style.backgroundColor = 'rgba(255, 255, 255, 0.02)';
    }
  };

  // Cleanup function
  const cleanup = () => {
    // Cancel animation frame first
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Then destroy PIXI application
    if (appRef.current) {
      // Remove all children from the stage
      if (appRef.current.stage) {
        while (appRef.current.stage.children.length > 0) {
          appRef.current.stage.removeChild(appRef.current.stage.children[0]);
        }
      }
      // Destroy the application
      appRef.current.destroy(true, true);
      appRef.current = null;
    }

    // Clear the container
    if (containerRef.current) {
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
    }

    // Reset clicking progress state
    setClickingAttachment(false);
    setCurrentAttachmentId(null);
    setClickProgress(0);
  };

  const handleBonusClick = (bonus) => {
    if (!worldConfig.show_bonuses_panel) return;
    setSelectedBonus(bonus);
    setUsageModalOpen(true);
  };

  const handleAttachmentClick = async (attachmentData, container) => {
    console.log('handleAttachmentClick called with:', attachmentData);
    console.log('worldConfig.are_attachments_clickable:', worldConfig.are_attachments_clickable);
    console.log('clickingAttachment:', clickingAttachment);
    
    if (!worldConfig.are_attachments_clickable || clickingAttachment) {
      console.log('Click blocked - not clickable or already clicking');
      return;
    }
    
    // Handle interactive zoom first
    if (isZooming) {
      console.log('Zoom animation in progress, ignoring click');
      return;
    }
    
    // Always zoom to 4x when clicking an attachment
    setCurrentZoomLevel(4);
    setZoomedAttachment(attachmentData.attachment_id);
    console.log(`Zooming to attachment ${attachmentData.attachment_id} at 4x`);
    
    // Zoom to attachment at 4x level (non-blocking)
    zoomToAttachmentInteractive(container, 4);
    
    // Reset auto rezoom timer
    resetZoomTimer();
    
    // Safety timeout to reset clickingAttachment if it gets stuck
    let safetyTimeout = null;
    
    try {
      console.log('Setting clickingAttachment to true');
      setClickingAttachment(true);
      
      safetyTimeout = setTimeout(() => {
        console.log('Safety timeout: resetting clickingAttachment state');
        setClickingAttachment(false);
      }, 10000); // 10 seconds timeout
      
      // Check if attachment has reached max click points
      const currentBaseValue = attachmentData.base_bonus;
      const maxPoints = worldConfig.max_attachment_click_points;
      
      if (currentBaseValue >= maxPoints) {
        // Keep the attachment ID and show full progress for maxed attachments
        setCurrentAttachmentId(attachmentData.attachment_id);
        setClickProgress(currentBaseValue);
        setClickingAttachment(false);
        return;
      }
      
      // Calculate new values locally
      const pointsToAdd = worldConfig.attachments_click_points_count;
      const newBaseValue = currentBaseValue + pointsToAdd;
      
      // Fallback: if localUserScore is still 0, use worldConfig.user_score
      const currentScore = localUserScore === 0 ? worldConfig.user_score : localUserScore;
      const newUserScore = currentScore + pointsToAdd;
      
      console.log('Score calculation:', {
        currentBaseValue,
        pointsToAdd,
        newBaseValue,
        currentLocalScore: localUserScore,
        worldConfigScore: worldConfig.user_score,
        currentScore,
        newUserScore,
        maxPoints
      });
      
      // Update local state immediately for instant feedback
      setLocalUserScore(newUserScore);
      setCurrentAttachmentId(attachmentData.attachment_id);
      
      // Add to pending updates for backend persistence
      setPendingScoreUpdates(prev => [...prev, {
        attachment_id: attachmentData.attachment_id,
        points_added: pointsToAdd,
        new_base_value: newBaseValue
      }]);
      
      // Update the attachment data in the container for future reference
      container.attachmentData.base_bonus = newBaseValue;
      
      // Update the score text with new base value
      const scoreText = container.children[1];
      scoreText.text = `+${newBaseValue}`;
      
      // Update progress to show the new value after the click
      setClickProgress(newBaseValue);
      
      // Update the worldConfig to reflect the new values for the user info bar
      if (worldConfig) {
        worldConfig.user_score = newUserScore;
      }
      
      // Set clickingAttachment to false immediately since we're not showing messages
      setClickingAttachment(false);
      
    } catch (error) {
      console.error('Error clicking attachment:', error);
      setClickingAttachment(false);
    } finally {
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
      }
      // Don't set clickingAttachment to false here - let it be handled by the timeout
    }
  };

  // Interactive zoom function
  const zoomToAttachmentInteractive = async (container, zoomLevel) => {
    if (!container || !appRef.current) return;
    
    setIsZooming(true);
    
    try {
      // Get container dimensions
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      
      // Calculate scale to fit within container
      const scaleX = containerWidth / worldConfig.width;
      const scaleY = containerHeight / worldConfig.height;
      const scale = Math.min(scaleX, scaleY, 1.2); // Allow scaling up to 1.2x for slightly bigger initial zoom
      
      // Calculate scaled dimensions
      const scaledWidth = worldConfig.width * scale;
      const scaledHeight = worldConfig.height * scale;
      
      // Use the local position of the container
      const localPosition = { x: container.x, y: container.y };
      
      // Calculate the center of the viewport
      const viewportCenterX = containerWidth / 2;
      const viewportCenterY = containerHeight / 2;
      
      // Calculate target scale and position
      const targetScale = scale * zoomLevel;
      const targetX = viewportCenterX - (localPosition.x * scale * zoomLevel);
      const targetY = viewportCenterY - (localPosition.y * scale * zoomLevel);
      
      const startX = appRef.current.stage.x;
      const startY = appRef.current.stage.y;
      const startScale = appRef.current.stage.scale.x;
      const startTime = Date.now();
      const duration = 800; // Faster animation for interactive zoom
      
      console.log(`Interactive zoom to level ${zoomLevel}x:`, {
        targetScale,
        targetX,
        targetY,
        currentScale: startScale
      });
      
      return new Promise((resolve) => {
        const animateZoom = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Easing function for smooth animation
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          
          // Update stage position and scale
          appRef.current.stage.x = startX + (targetX - startX) * easeProgress;
          appRef.current.stage.y = startY + (targetY - startY) * easeProgress;
          const newScale = startScale + (targetScale - startScale) * easeProgress;
          appRef.current.stage.scale.set(newScale);
          
          if (progress < 1) {
            requestAnimationFrame(animateZoom);
          } else {
            console.log('Interactive zoom completed:', {
              x: appRef.current.stage.x,
              y: appRef.current.stage.y,
              scale: appRef.current.stage.scale.x
            });
            resolve();
          }
        };
        
        animateZoom();
      });
    } finally {
      setIsZooming(false);
    }
  };
  
  // Function to rezoom to the whole world
  const rezoomToWorld = async () => {
    if (!appRef.current || isZooming) return;
    
    setIsZooming(true);
    
    try {
      // Get container dimensions
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      
      // Calculate scale to fit within container (exactly same as initial setup)
      const scaleX = containerWidth / worldConfig.width;
      const scaleY = containerHeight / worldConfig.height;
      const scale = Math.min(scaleX, scaleY, 1.2); // Allow scaling up to 1.2x for slightly bigger initial zoom
      
      // Calculate scaled dimensions (exactly same as initial setup)
      const scaledWidth = worldConfig.width * scale;
      const scaledHeight = worldConfig.height * scale;
      
      const startX = appRef.current.stage.x;
      const startY = appRef.current.stage.y;
      const startScale = appRef.current.stage.scale.x;
      const targetScale = scale;
      // Use exactly same positioning logic as initial setup
      const targetX = (containerWidth - scaledWidth) / 2;
      const targetY = (containerHeight - scaledHeight) / 2;
      const startTime = Date.now();
      const duration = 1000;
      
      console.log('Rezooming to world view with exact same calculations as initial setup');
      
      return new Promise((resolve) => {
        const animateZoom = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Easing function for smooth animation
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          
          // Update stage position and scale
          appRef.current.stage.x = startX + (targetX - startX) * easeProgress;
          appRef.current.stage.y = startY + (targetY - startY) * easeProgress;
          const newScale = startScale + (targetScale - startScale) * easeProgress;
          appRef.current.stage.scale.set(newScale);
          
          if (progress < 1) {
            requestAnimationFrame(animateZoom);
          } else {
            console.log('Rezoom to world completed');
            setCurrentZoomLevel(1);
            setZoomedAttachment(null);
            // Don't hide clicking progress here - let it be handled by the timeout in handleAttachmentClick
            setCurrentAttachmentId(null);
            resolve();
          }
        };
        
        animateZoom();
      });
    } finally {
      setIsZooming(false);
    }
  };
  
  // Function to reset zoom timer
  const resetZoomTimer = () => {
    // Clear existing timer
    if (zoomTimer) {
      clearTimeout(zoomTimer);
    }
    
    // Set new timer for auto rezoom (5 seconds of inactivity)
    const newTimer = setTimeout(() => {
      console.log('Auto rezoom timer triggered');
      rezoomToWorld();
    }, 5000);
    
    setZoomTimer(newTimer);
  };

  const handleUseBonus = async () => {
    if (!selectedBonus) return;
    
    try {
      setUsingBonus(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`/users/use_bonus/${selectedBonus.attachment_bonus_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Close modal and show success message
        setUsageModalOpen(false);
        setSelectedBonus(null);
        
        // Show success message in the panel
        const bonusMessage = t('bonus_usage_sent');
        console.log('Setting bonus usage message:', bonusMessage);
        setSuccessMessage(bonusMessage);
        
        // Clear message after 5 seconds
        setTimeout(() => {
          console.log('Clearing bonus usage message after timeout');
          setSuccessMessage('');
        }, 5000);
        
        // Refresh the world config to update the bonus list
        if (onRefresh) {
          // Delay the refresh to allow the message to be visible
          setTimeout(() => {
            onRefresh();
          }, 1000);
        }
      } else {
        const error = await response.json();
        alert(`Error using bonus: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error using bonus:', error);
      alert('Failed to use bonus. Please try again.');
    } finally {
      setUsingBonus(false);
    }
  };

  const handleCancelUsage = () => {
    setUsageModalOpen(false);
    setSelectedBonus(null);
  };

  // Function to persist pending score updates to backend
  const persistScoreUpdates = async () => {
    if (pendingScoreUpdates.length === 0) {
      console.log('No pending score updates to persist');
      return;
    }

    // Prevent multiple simultaneous calls
    if (persistingUpdates) {
      console.log('Already persisting updates, skipping duplicate call');
      return;
    }

    try {
      setPersistingUpdates(true);
      console.log('Persisting score updates to backend:', pendingScoreUpdates);
      const token = localStorage.getItem('adminToken');
      
      // Send all pending updates to backend
      for (const update of pendingScoreUpdates) {
        const response = await fetch(`/users/increase_attachment_base_value/${update.attachment_id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.error('Failed to persist score update for attachment:', update.attachment_id);
        }
      }

      console.log('Successfully persisted all score updates');
      setPendingScoreUpdates([]); // Clear pending updates after successful persistence
    } catch (error) {
      console.error('Error persisting score updates:', error);
    } finally {
      setPersistingUpdates(false);
    }
  };

  // Function to check if bonus expires soon (within 24 hours)
  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const now = new Date();
    const expiration = new Date(expirationDate);
    const timeDiff = expiration.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);
    return hoursDiff <= 24 && hoursDiff > 0;
  };

  // Function to check if bonus is expired
  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    const now = new Date();
    const expiration = new Date(expirationDate);
    return expiration < now;
  };

  // Function to format expiration time
  const formatExpirationTime = (expirationDate) => {
    if (!expirationDate) return t('never_expires');
    
    const now = new Date();
    const expiration = new Date(expirationDate);
    const timeDiff = expiration.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return t('expired');
    }
    
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
    const hoursDiff = Math.floor((timeDiff % (1000 * 3600 * 24)) / (1000 * 3600));
    const minutesDiff = Math.floor((timeDiff % (1000 * 3600)) / (1000 * 60));
    
    if (daysDiff > 0) {
      return `${daysDiff} ${t('days')} ${hoursDiff} ${t('hours')}`;
    } else if (hoursDiff > 0) {
      return `${hoursDiff} ${t('hours')} ${minutesDiff} ${t('minutes')}`;
    } else {
      return `${minutesDiff} ${t('minutes')}`;
    }
  };

  // Function to get expiration color
  const getExpirationColor = (expirationDate) => {
    if (!expirationDate) return '#4CAF50'; // Green for no expiration
    if (isExpired(expirationDate)) return '#f44336'; // Red for expired
    if (isExpiringSoon(expirationDate)) return '#ff9800'; // Orange for expiring soon
    return '#2196F3'; // Blue for normal
  };

  // Function to get expiration icon
  const getExpirationIcon = (expirationDate) => {
    if (!expirationDate) return '♾️'; // Infinity for no expiration
    if (isExpired(expirationDate)) return '⏰'; // Clock for expired
    if (isExpiringSoon(expirationDate)) return '⚠️'; // Warning for expiring soon
    return '📅'; // Calendar for normal
  };

  useEffect(() => {
    if (!worldConfig || !containerRef.current) return;

    // Set mounted flag
    isMountedRef.current = true;

    // Debug: Log the worldConfig to see if clickable settings are present
    console.log('PixiWorldViewer - worldConfig:', worldConfig);
    console.log('PixiWorldViewer - user_score from worldConfig:', worldConfig.user_score);
    console.log('PixiWorldViewer - localUserScore current value:', localUserScore);
    console.log('PixiWorldViewer - are_attachments_clickable:', worldConfig.are_attachments_clickable);
    console.log('PixiWorldViewer - attachments_click_points_count:', worldConfig.attachments_click_points_count);
    console.log('PixiWorldViewer - max_attachment_click_points:', worldConfig.max_attachment_click_points);

    // Clean up any existing PIXI application
    cleanup();

    // Initialize PIXI
    const initPixi = async () => {
      try {
        // Get the container dimensions
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;
        
        // Calculate scale to fit within container
        const scaleX = containerWidth / worldConfig.width;
        const scaleY = containerHeight / worldConfig.height;
        const scale = Math.min(scaleX, scaleY, 1.2); // Allow scaling up to 1.2x for slightly bigger initial zoom
        
        // Calculate scaled dimensions
        const scaledWidth = worldConfig.width * scale;
        const scaledHeight = worldConfig.height * scale;

        // Create PIXI application with full container size
        const app = new PIXI.Application({
          width: containerWidth,
          height: containerHeight,
          backgroundColor: 0x000000,
          antialias: true,
          resolution: 1,
          autoDensity: false,
        });

        // Check if component is still mounted after async operations
        if (!isMountedRef.current) {
          app.destroy(true, true);
          return;
        }

        // Ensure the application is ready and view is available
        if (!app.view) {
          console.error('PIXI Application view is not available');
          return;
        }

        // Clear any existing content
        while (containerRef.current.firstChild) {
          containerRef.current.removeChild(containerRef.current.firstChild);
        }

        // Add canvas to container
        containerRef.current.appendChild(app.view);
        appRef.current = app;

        // Scale the entire stage to fit the world
        app.stage.scale.set(scale);
        
        // Center the stage in the container
        app.stage.x = (containerWidth - scaledWidth) / 2;
        app.stage.y = (containerHeight - scaledHeight) / 2;

        // Helper function to get full texture URL
        const getTextureUrl = (path) => {
          // Remove any leading slashes to avoid double slashes
          const cleanPath = path.replace(/^\/+/, '');
          return `/${cleanPath}`;
        };

        // Load textures and create sprites
        try {
          // Load background
          console.log('Loading background texture:', getTextureUrl(worldConfig.background.texture));
          const backgroundTexture = await PIXI.Assets.load(getTextureUrl(worldConfig.background.texture));
          const background = new PIXI.Sprite(backgroundTexture);
          background.anchor.set(worldConfig.background.anchor.x, worldConfig.background.anchor.y);
          background.x = worldConfig.background.x;
          background.y = worldConfig.background.y;
          app.stage.addChild(background);

          // Load attachments
          let newAttachments = [];
          console.log('=== Starting to load attachments ===');
          for (const attachment of worldConfig.attachments) {
            console.log('Loading attachment texture:', getTextureUrl(attachment.texture));
            const texture = await PIXI.Assets.load(getTextureUrl(attachment.texture));
            
            // Create a container for the sprite and its text
            const container = new PIXI.Container();
            container.x = attachment.x;
            container.y = attachment.y;
            container.rotation = attachment.rotation * (Math.PI / 180);
            
            // Store the attachment data for reference
            container.attachmentData = attachment;
            
            // Create and add the sprite to the container
            const sprite = new PIXI.Sprite(texture);
            // Set anchor to bottom center
            sprite.anchor.set(0.5, 1);

            // Resize sprite using the resize_height from backend
            if (attachment.resize_scale) {
              sprite.scale.set(attachment.resize_scale);
            }

            // Add animation properties to the sprite
            sprite.animationData = {
              originalScale: { x: sprite.scale.x, y: sprite.scale.y },
              time: Math.random() * Math.PI * 2,
              speed: 0.005 + Math.random() * 0.005,
              stretchAmount: 0.1 + Math.random() * 0.1,
              bounceAmount: 0.03 + Math.random() * 0.1,
            };

            // Set initial alpha to 0 only for new attachments
            sprite.alpha = attachment.is_new_attachment ? 0 : 1;
            console.log('Sprite alpha:', sprite.alpha);
            container.addChild(sprite);

            // Create score text with random bright color
            const textColor = getRandomBrightColor();
            const scoreText = new PIXI.Text(`+${attachment.base_bonus}`, {
              fontFamily: 'Comic Sans MS, cursive',
              fontSize: 28,
              fontWeight: 'bold',
              fill: textColor,
              align: 'center',
              stroke: 0x000000,
              strokeThickness: 6,
              dropShadow: true,
              dropShadowColor: 0x000000,
              dropShadowBlur: 4,
              dropShadowDistance: 2,
              padding: 10
            });
            scoreText.anchor.set(0.5);
            scoreText.y = -sprite.height;
            // Set initial alpha to 0 only for new attachments
            scoreText.alpha = attachment.is_new_attachment ? 0 : 1;
            container.addChild(scoreText);

            // Make container interactive if attachments are clickable
            if (worldConfig.are_attachments_clickable) {
              console.log('Setting up interactive attachment:', attachment.attachment_id);
              container.eventMode = 'static';
              container.cursor = 'pointer';
              
              // Add hover effects
              container.on('pointerover', () => {
                console.log('Attachment hover:', attachment.attachment_id);
                if (!clickingAttachment) {
                  sprite.scale.set(
                    sprite.animationData.originalScale.x * 1.1,
                    sprite.animationData.originalScale.y * 1.1
                  );
                }
              });
              
              container.on('pointerout', () => {
                console.log('Attachment hover out:', attachment.attachment_id);
                if (!clickingAttachment) {
                  sprite.scale.set(
                    sprite.animationData.originalScale.x,
                    sprite.animationData.originalScale.y
                  );
                }
              });
              
              // Add click handler
              container.on('pointerdown', () => {
                console.log('Attachment clicked:', attachment.attachment_id);
                handleAttachmentClick(attachment, container);
              });
            } else {
              console.log('Attachments are not clickable for this client');
            }

            // Add the container to the stage
            app.stage.addChild(container);

            console.log(`Sprite position - x: ${container.x}, y: ${container.y}, is_new: ${attachment.is_new_attachment}, visible: ${!attachment.is_new_attachment}, alpha: ${sprite.alpha}`);

            // Store reference to new attachments
            if (attachment.is_new_attachment) {
              newAttachments.push(container);
              console.log('Found new attachment:', {
                x: container.x,
                y: container.y,
                attachmentData: attachment
              });
            }
          }

          // Function to animate sprite appearance
          const animateSpriteAppearance = (container, duration = 1000) => {
            return new Promise((resolve) => {
              const sprite = container.children[0];
              const scoreText = container.children[1];
              const startTime = Date.now();

              const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Easing function for smooth animation
                const easeProgress = 1 - Math.pow(1 - progress, 3);

                // Fade in both sprite and score text
                sprite.alpha = easeProgress;
                scoreText.alpha = easeProgress;

                // Add a slight scale animation during appearance
                const scaleFactor = 1 + (1 - easeProgress) * 0.2; // Start 20% larger
                sprite.scale.x = sprite.animationData.originalScale.x * scaleFactor;
                sprite.scale.y = sprite.animationData.originalScale.y * scaleFactor;

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  // Reset to original scale
                  sprite.scale.x = sprite.animationData.originalScale.x;
                  sprite.scale.y = sprite.animationData.originalScale.y;
                  resolve();
                }
              };

              animate();
            });
          };

          // Function to zoom to a specific container
          const zoomToContainer = (container, duration = 1000) => {
            if (!container || !appRef.current) return;

            // Use the local position of the container (not global)
            const localPosition = { x: container.x, y: container.y };
            console.log('Container local position:', localPosition);

            // Calculate the center of the viewport (scaled dimensions)
            const viewportCenterX = scaledWidth / 2;
            const viewportCenterY = scaledHeight / 2;

            // Calculate the offset needed to center the container
            // Since the stage is already scaled, we need to account for that
            const targetScale = scale * 4; // Zoom in 4x from the base scale (increased from 2x)
            const targetX = viewportCenterX - (localPosition.x * scale * 4);
            const targetY = viewportCenterY - (localPosition.y * scale * 4);
            
            const startX = appRef.current.stage.x;
            const startY = appRef.current.stage.y;
            const startScale = appRef.current.stage.scale.x;
            const startTime = Date.now();

            console.log('=== Zoom Animation Details ===');
            console.log('Container details:', {
              localPosition: localPosition,
              viewportCenter: { x: viewportCenterX, y: viewportCenterY },
              scaledDimensions: { width: scaledWidth, height: scaledHeight },
              baseScale: scale
            });
            console.log('Target position:', {
              x: targetX,
              y: targetY,
              scale: targetScale
            });
            console.log('Start position:', {
              x: startX,
              y: startY,
              scale: startScale
            });

            return new Promise((resolve) => {
              const animateZoom = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Easing function for smooth animation
                const easeProgress = 1 - Math.pow(1 - progress, 3);

                // Update stage position and scale
                appRef.current.stage.x = startX + (targetX - startX) * easeProgress;
                appRef.current.stage.y = startY + (targetY - startY) * easeProgress;
                const newScale = startScale + (targetScale - startScale) * easeProgress;
                appRef.current.stage.scale.set(newScale);

                if (progress < 1) {
                  requestAnimationFrame(animateZoom);
                } else {
                  console.log('Final zoom position:', {
                    x: appRef.current.stage.x,
                    y: appRef.current.stage.y,
                    scale: appRef.current.stage.scale.x
                  });
                  resolve();
                }
              };

              animateZoom();
            });
          };

          // Function to zoom out to show the whole world
          const zoomOutToWorld = (duration = 1000) => {
            if (!appRef.current) return;

            const startX = appRef.current.stage.x;
            const startY = appRef.current.stage.y;
            const startScale = appRef.current.stage.scale.x;
            const targetScale = scale; // Return to the base scale
            // Use same centering logic as initial setup
            const targetX = (containerWidth - scaledWidth) / 2;
            const targetY = (containerHeight - scaledHeight) / 2;
            const startTime = Date.now();

            return new Promise((resolve) => {
              const animateZoom = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Easing function for smooth animation
                const easeProgress = 1 - Math.pow(1 - progress, 3);

                // Update stage position and scale
                appRef.current.stage.x = startX + (targetX - startX) * easeProgress;
                appRef.current.stage.y = startY + (targetY - startY) * easeProgress;
                const newScale = startScale + (targetScale - startScale) * easeProgress;
                appRef.current.stage.scale.set(newScale);

                if (progress < 1) {
                  requestAnimationFrame(animateZoom);
                } else {
                  console.log('Final zoom out position:', {
                    x: appRef.current.stage.x,
                    y: appRef.current.stage.y,
                    scale: appRef.current.stage.scale.x
                  });
                  resolve();
                }
              };

              animateZoom();
            });
          };

          // Function to wait for a specified duration
          const wait = (duration) => {
            return new Promise(resolve => setTimeout(resolve, duration));
          };

          // If we found new attachments, create a sequence of zoom animations
          if (newAttachments.length > 0) {
            console.log(`Found ${newAttachments.length} new attachments`);
            console.log('New attachments details:', newAttachments.map((att, index) => ({
              index,
              x: att.x,
              y: att.y,
              attachmentData: att.attachmentData
            })));
            
            // Create an async function to handle the sequence
            const runZoomSequence = async () => {
              for (let i = 0; i < newAttachments.length; i++) {
                const attachment = newAttachments[i];
                console.log(`=== Zooming to attachment ${i + 1}/${newAttachments.length} ===`);
                console.log('Attachment position:', {
                  local: { x: attachment.x, y: attachment.y },
                  attachmentData: attachment.attachmentData
                });
                
                // Zoom in to the attachment
                await zoomToContainer(attachment);
                
                // Animate sprite appearance
                console.log('Animating sprite appearance');
                await animateSpriteAppearance(attachment);
                
                // Wait for 5 seconds (increased from 2 seconds)
                console.log('Waiting 5 seconds...');
                await wait(5000);
                
                // Zoom out to show the whole world
                console.log('Zooming out to show whole world');
                await zoomOutToWorld();
                
                // Wait for 1 second before next attachment
                await wait(1000);
              }
            };

            // Start the sequence after a short initial delay
            setTimeout(() => {
              runZoomSequence();
            }, 1000);
          } else {
            console.log('No new attachments found to zoom to');
            console.log('All attachments:', worldConfig.attachments.map((att, index) => ({
              index,
              x: att.x,
              y: att.y,
              is_new_attachment: att.is_new_attachment
            })));
          }

          // Animation loop
          const animate = () => {
            // Check if component is still mounted and app exists
            if (!isMountedRef.current || !appRef.current) return;

            // Update each sprite's animation
            if (appRef.current.stage) {
              appRef.current.stage.children.forEach(container => {
                if (container instanceof PIXI.Container && container.children.length > 0) {
                  const sprite = container.children[0];
                  if (sprite instanceof PIXI.Sprite && sprite.animationData) {
                    const data = sprite.animationData;
                    data.time += data.speed;

                    // Calculate stretch and bounce
                    const stretch = Math.sin(data.time) * data.stretchAmount;
                    const bounce = Math.sin(data.time * 2) * data.bounceAmount;

                    // Apply animations while keeping bottom anchored
                    sprite.scale.x = data.originalScale.x * (1 + stretch);
                    sprite.scale.y = data.originalScale.y * (1 - stretch);
                    // Move sprite up and down from its bottom anchor
                    sprite.y = bounce * sprite.height;

                    // Update score text position to follow sprite
                    if (container.children[1] instanceof PIXI.Text) {
                      container.children[1].y = -sprite.height;
                    }
                  }
                }
              });
            }

            // Schedule next frame if still mounted
            if (isMountedRef.current) {
              animationRef.current = requestAnimationFrame(animate);
            }
          };

          // Start animation
          animate();

          // Log the stage bounds for debugging
          console.log('Stage bounds:', {
            width: app.stage.width,
            height: app.stage.height,
            children: app.stage.children.length,
            background: {
              x: background.x,
              y: background.y,
              width: background.width,
              height: background.height,
              anchor: background.anchor
            },
            worldConfig: {
              width: worldConfig.width,
              height: worldConfig.height
            }
          });

        } catch (error) {
          console.error('Error loading world:', error);
          // Show error message
          const errorText = new PIXI.Text('Error loading world', {
            fontFamily: 'Arial',
            fontSize: 24,
            fill: 0xff0000,
            align: 'center'
          });
          errorText.anchor.set(0.5);
          errorText.x = worldConfig.width / 2;
          errorText.y = worldConfig.height / 2;
          app.stage.addChild(errorText);
        }

      } catch (error) {
        console.error('Error initializing PIXI:', error);
      }
    };

    initPixi();

    // Cleanup on unmount or when worldConfig changes
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [worldConfig]);

  // Handle component unmount or when user leaves
  useEffect(() => {
    return () => {
      // Only call onBeforeClose callback if provided
      if (onBeforeClose) {
        onBeforeClose();
      }
    };
  }, [onBeforeClose]);

  // Update local score when worldConfig changes
  useEffect(() => {
    console.log('Effect triggered - worldConfig?.user_score:', worldConfig?.user_score);
    if (worldConfig?.user_score !== undefined) {
      // Only update if worldConfig score is higher than current local score
      // This prevents losing local updates when worldConfig changes
      if (worldConfig.user_score > localUserScore) {
        console.log('Updating localUserScore from worldConfig:', worldConfig.user_score);
        setLocalUserScore(worldConfig.user_score);
      } else {
        console.log('Keeping localUserScore as it is higher than worldConfig:', localUserScore);
      }
    } else {
      console.log('worldConfig.user_score is undefined, not updating localUserScore');
    }
  }, [worldConfig?.user_score, localUserScore]);

  // Debug: Log when component mounts and worldConfig changes
  useEffect(() => {
    console.log('Component mounted/updated - worldConfig:', worldConfig);
    console.log('Initial localUserScore:', localUserScore);
    
    // Reset clicking progress state when worldConfig changes
    setClickingAttachment(false);
    setCurrentAttachmentId(null);
    setClickProgress(0);
    
    // Initialize localUserScore when worldConfig is first loaded or when it changes
    if (worldConfig && worldConfig.user_score !== undefined) {
      console.log('Initializing/updating localUserScore from worldConfig:', worldConfig.user_score);
      setLocalUserScore(worldConfig.user_score);
    }
  }, [worldConfig]);

  // Cleanup zoom timer on unmount
  useEffect(() => {
    return () => {
      if (zoomTimer) {
        clearTimeout(zoomTimer);
      }
    };
  }, [zoomTimer]);

  // Reset progress when currentAttachmentId changes
  useEffect(() => {
    if (currentAttachmentId && worldConfig?.attachments) {
      // Find the current attachment and set its progress
      const currentAttachment = worldConfig.attachments.find(
        att => att.attachment_id === currentAttachmentId
      );
      if (currentAttachment) {
        console.log('Updating progress for attachment:', {
          attachmentId: currentAttachmentId,
          baseBonus: currentAttachment.base_bonus,
          maxPoints: worldConfig.max_attachment_click_points
        });
        setClickProgress(currentAttachment.base_bonus);
      }
    } else if (!currentAttachmentId) {
      // Reset progress when no attachment is selected
      setClickProgress(0);
    }
  }, [currentAttachmentId, worldConfig?.attachments]);

  useImperativeHandle(ref, () => ({
    persistScoreUpdates
  }));

  return (
    <div style={{ position: 'relative', display: 'flex', height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}>
      {/* CSS Animation */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      
      {/* Left Side Panel - User Info, Bonuses, and Controls */}
      <div style={{
        width: '320px',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        borderRight: '1px solid #4CAF50',
        padding: '15px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxSizing: 'border-box',
        backdropFilter: 'blur(10px)'
      }}>
        {/* User Level and Points Info - Always visible at top */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
          border: '1px solid rgba(76, 175, 80, 0.2)',
          borderRadius: '12px',
          padding: '20px',
          color: 'white',
          textAlign: 'center',
          flexShrink: 0 // Prevent shrinking
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Level Badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '10px 16px',
              background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
              borderRadius: '25px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
            }}>
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>🏆</span>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
                {t('level')}: {worldConfig.user_level}
              </span>
            </div>
            
            {/* Points Badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '10px 16px',
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              borderRadius: '25px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)'
            }}>
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>⭐</span>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
                {localUserScore} {t('points')}
              </span>
            </div>
          </div>
        </div>
        
        {/* Progress Bar - Moved to left panel */}
        {worldConfig.are_attachments_clickable && currentZoomLevel > 1 && (
          <div style={{
            background: 'rgba(0, 0, 0, 0.8)',
            border: '1px solid #4CAF50',
            borderRadius: '8px',
            padding: '15px',
            color: 'white',
            fontSize: '12px',
            marginTop: '15px',
            marginBottom: '15px',
            animation: 'fadeIn 0.3s ease-in'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <span style={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#4CAF50'
              }}>
                {currentAttachmentId ? t('progress') : t('click_progress')}
              </span>
              <span style={{
                fontSize: '12px',
                color: '#ccc'
              }}>
                {clickProgress} / {worldConfig.max_attachment_click_points}
              </span>
            </div>
            
            <div style={{
              width: '100%',
              height: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              overflow: 'hidden',
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}>
              <div style={{
                width: `${Math.max((clickProgress / worldConfig.max_attachment_click_points) * 100, 0)}%`,
                height: '100%',
                backgroundColor: clickProgress >= worldConfig.max_attachment_click_points ? '#FFD700' : '#4CAF50',
                transition: 'width 0.3s ease, background-color 0.3s ease',
                borderRadius: '6px',
                minWidth: '0%'
              }} />
            </div>
            
            <div style={{
              color: '#ccc',
              marginTop: '6px',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              {currentAttachmentId ? 
                (clickProgress >= worldConfig.max_attachment_click_points ? 
                  t('max_reached') :
                  t('click_attachment')
                ) : 
                t('click_attachment_to_start')
              }
            </div>
          </div>
        )}
        
        {/* User Bonuses Panel - Scrollable section */}
        {worldConfig.show_bonuses_panel && (
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: 0, // Allow flex item to shrink
            marginTop: '15px',
            marginBottom: '15px'
          }}>
            <h2 style={{
              color: '#4CAF50',
              margin: '0 0 15px 0',
              fontSize: '20px',
              fontWeight: 'bold',
              textAlign: 'center',
              borderBottom: '1px solid #4CAF50',
              paddingBottom: '8px',
              flexShrink: 0 // Keep header visible
            }}>
              {t('active_bonuses')}
            </h2>
            
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '5px' // Space for scrollbar
            }}>
              {worldConfig.user_bonuses && worldConfig.user_bonuses.length > 0 ? (
                worldConfig.user_bonuses.map((bonus, index) => (
                  <div 
                    key={index} 
                    style={{
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      color: 'white',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, background-color 0.3s ease',
                      fontSize: '13px',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onClick={() => handleBonusClick(bonus)}
                    onMouseEnter={(e) => {
                      handleBonusHover(e, true);
                    }}
                    onMouseLeave={(e) => {
                      handleBonusHover(e, false);
                    }}
                  >
                    {/* Bonus type icon and header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '10px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, #4CAF50 0%, #45a049 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                          transition: 'none'
                        }}>
                          {bonus.bonus_type === 'points' ? '🎯' : 
                           bonus.bonus_type === 'discount' ? '💰' : 
                           bonus.bonus_type === 'free_item' ? '🎁' : '⭐'}
                        </div>
                        <span style={{
                          fontWeight: 'bold',
                          fontSize: '14px',
                          color: '#4CAF50',
                          transition: 'none'
                        }}>
                          {bonus.bonus_type === 'points' ? t('bonus_type_points') : 
                           bonus.bonus_type === 'discount' ? t('bonus_type_discount') : 
                           bonus.bonus_type === 'free_item' ? t('bonus_type_free_item') : t('bonus_type_general')}
                        </span>
                        <div style={{
                          minWidth: '40px',
                          height: '28px',
                          borderRadius: '14px',
                          background: `linear-gradient(135deg, #FFD700 0%, #FFA500 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: 'black',
                          boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)',
                          transition: 'none',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          padding: '0 8px'
                        }}>
                          {bonus.calculated_bonus}
                          {bonus.bonus_type === 'discount' ? '%' : 
                           bonus.bonus_type === 'free_item' ? t('currency') : ''}
                        </div>
                      </div>
                    </div>
                    
                    {/* Bonus details */}
                    <div style={{
                      fontSize: '12px',
                      lineHeight: '1.4',
                      position: 'relative',
                      zIndex: 1,
                      transition: 'none'
                    }}>
                      {/* Enhanced expiration display */}
                      <div style={{ 
                        marginBottom: '6px', 
                        transition: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span style={{
                          color: getExpirationColor(bonus.expires_at),
                          fontWeight: isExpiringSoon(bonus.expires_at) || isExpired(bonus.expires_at) ? 'bold' : 'normal'
                        }}>
                          {t('expired_label')}{formatExpirationTime(bonus.expires_at)}
                        </span>
                      </div>
                      
                      {/* Show warning for expiring soon bonuses */}
                      {isExpiringSoon(bonus.expires_at) && (
                        <div style={{
                          backgroundColor: 'rgba(255, 152, 0, 0.2)',
                          border: '1px solid rgba(255, 152, 0, 0.5)',
                          borderRadius: '4px',
                          padding: '4px 6px',
                          marginTop: '4px',
                          fontSize: '11px',
                          color: '#ff9800',
                          fontWeight: 'bold',
                          textAlign: 'center'
                        }}>
                          ⚠️ {t('expires_soon')}
                        </div>
                      )}
                      
                      {/* Show expired indicator */}
                      {isExpired(bonus.expires_at) && (
                        <div style={{
                          backgroundColor: 'rgba(244, 67, 54, 0.2)',
                          border: '1px solid rgba(244, 67, 54, 0.5)',
                          borderRadius: '4px',
                          padding: '4px 6px',
                          marginTop: '4px',
                          fontSize: '11px',
                          color: '#f44336',
                          fontWeight: 'bold',
                          textAlign: 'center'
                        }}>
                          ⏰ {t('expired')}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  color: '#ccc',
                  fontSize: '14px',
                  padding: '30px 20px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '8px',
                  border: '1px dashed rgba(76, 175, 80, 0.3)'
                }}>
                  <div style={{
                    fontSize: '24px',
                    marginBottom: '8px'
                  }}>
                    🎁
                  </div>
                  {t('no_active_bonuses')}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Back to Search Button - Always visible at bottom */}
        <div style={{
          padding: '15px 0',
          borderTop: '1px solid rgba(76, 175, 80, 0.3)',
          flexShrink: 0 // Prevent shrinking
        }}>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '16px 24px',
              backgroundColor: '#d32f2f',
              color: 'white',
              border: '2px solid #b71c1c',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#b71c1c';
              e.target.style.borderColor = '#8e0000';
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 8px 20px rgba(211, 47, 47, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#d32f2f';
              e.target.style.borderColor = '#b71c1c';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(211, 47, 47, 0.3)';
            }}
          >
            {t('back_to_search')}
          </button>
        </div>
      </div>

      {/* Game World Container - Full Right Side */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Information Messages - Compact floating notifications */}
        {(successMessage || clickingAttachment || (worldConfig?.are_attachments_clickable && !successMessage && !clickingAttachment)) && (
          <div style={{
            position: 'absolute',
            top: '15px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            maxWidth: '600px',
            width: 'auto',
            pointerEvents: 'none'
          }}>
            {/* Welcome Message */}
            {(!successMessage && !clickingAttachment && worldConfig?.are_attachments_clickable) && (
              <div style={{
                backgroundColor: 'rgba(33, 150, 243, 0.95)',
                border: '1px solid #2196F3',
                borderRadius: '6px',
                padding: '10px 15px',
                marginBottom: '8px',
                color: 'white',
                fontSize: '13px',
                fontWeight: 'bold',
                animation: 'fadeIn 0.3s ease-in',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(10px)',
                pointerEvents: 'none'
              }}>
                {t('click_attachment_to_see')}
              </div>
            )}
            
            {/* Success Message */}
            {successMessage && (
              <div style={{
                backgroundColor: 'rgba(76, 175, 80, 0.95)',
                border: '1px solid #4CAF50',
                borderRadius: '6px',
                padding: '10px 15px',
                marginBottom: '8px',
                color: 'white',
                fontSize: '13px',
                fontWeight: 'bold',
                animation: 'fadeIn 0.3s ease-in',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(10px)',
                pointerEvents: 'none'
              }}>
                {successMessage}
              </div>
            )}
            
            {/* Clicking Attachment Indicator */}
            {clickingAttachment && (
              <div style={{
                backgroundColor: 'rgba(255, 193, 7, 0.95)',
                border: '1px solid #FFC107',
                borderRadius: '6px',
                padding: '10px 15px',
                color: 'white',
                fontSize: '13px',
                fontWeight: 'bold',
                animation: 'fadeIn 0.3s ease-in',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(10px)',
                pointerEvents: 'none'
              }}>
                {t('clicking_attachment')}
              </div>
            )}
          </div>
        )}
        
        {/* Game World Content - Full height */}
        <div 
          ref={containerRef} 
          style={{ 
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#000000',
            display: 'block'
          }}
        />
      </div>

      {/* Bonus Usage Modal */}
      {usageModalOpen && selectedBonus && worldConfig.show_bonuses_panel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            border: '2px solid #4CAF50',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            color: 'white',
            textAlign: 'center'
          }}>
            <h2 style={{
              color: '#4CAF50',
              margin: '0 0 20px 0',
              fontSize: '28px',
              fontWeight: 'bold'
            }}>
              {t('use_bonus')}
            </h2>
            
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid #4CAF50',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '30px'
            }}>
              <h3 style={{
                margin: '0 0 15px 0',
                fontSize: '22px',
                fontWeight: 'bold',
                color: '#4CAF50'
              }}>
                {selectedBonus.bonus_type === 'points' ? t('bonus_type_points') : 
                 selectedBonus.bonus_type === 'discount' ? t('bonus_type_discount') : 
                 selectedBonus.bonus_type === 'free_item' ? t('bonus_type_free_item') : t('bonus_type_general')}
              </h3>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <span style={{ fontSize: '16px', color: '#ccc' }}>{t('bonus_amount')}:</span>
                <span style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {selectedBonus.calculated_bonus}
                  {selectedBonus.bonus_type === 'discount' ? '%' : 
                   selectedBonus.bonus_type === 'free_item' ? t('currency') : ''}
                </span>
              </div>
              
              <div style={{
                fontSize: '14px',
                color: getExpirationColor(selectedBonus.expires_at)
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '18px' }}>
                    {getExpirationIcon(selectedBonus.expires_at)}
                  </span>
                  <strong>{t('expires')}:</strong> {formatExpirationTime(selectedBonus.expires_at)}
                </div>
                
                {/* Show warning for expiring soon bonuses */}
                {isExpiringSoon(selectedBonus.expires_at) && (
                  <div style={{
                    backgroundColor: 'rgba(255, 152, 0, 0.2)',
                    border: '1px solid rgba(255, 152, 0, 0.5)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    color: '#ff9800',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    marginTop: '8px'
                  }}>
                    ⚠️ {t('expires_soon')}
                  </div>
                )}
                
                {/* Show expired indicator */}
                {isExpired(selectedBonus.expires_at) && (
                  <div style={{
                    backgroundColor: 'rgba(244, 67, 54, 0.2)',
                    border: '1px solid rgba(244, 67, 54, 0.5)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    color: '#f44336',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    marginTop: '8px'
                  }}>
                    ⏰ {t('expired')}
                  </div>
                )}
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleUseBonus}
                disabled={usingBonus}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: usingBonus ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: usingBonus ? 0.6 : 1,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!usingBonus) {
                    e.target.style.backgroundColor = '#45a049';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!usingBonus) {
                    e.target.style.backgroundColor = '#4CAF50';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                {usingBonus ? t('using') : t('use_bonus')}
              </button>
              
              <button
                onClick={handleCancelUsage}
                disabled={usingBonus}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#ccc',
                  border: '2px solid #ccc',
                  borderRadius: '6px',
                  cursor: usingBonus ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: usingBonus ? 0.6 : 1,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!usingBonus) {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.borderColor = '#fff';
                    e.target.style.color = '#fff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!usingBonus) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#ccc';
                    e.target.style.color = '#ccc';
                  }
                }}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PixiWorldViewer; 